import { t } from 'i18next';
import { Vector3 } from 'three';

import {
  CellGridVolumeErrorCodes,
  DynamicAssetCommonPropertiesModel,
} from '../../../../../typings/api/skymap/rest/v0/.common';
import { formattedDateTime } from '../../../utils/dateUtils';
import { isDefined } from '../../../utils/variables';
import { AssetManager } from '../../asset-manager';
import { ProjectGeodataManager } from '../../project-geodata-manager';
import { TerrainModel } from '../terrain-model';

/**
 *  Convert the volume error code to a readable error.
 *
 *  @param code - The error code that is attached to the SkymapVolume
 */
const errorTexts = (code: CellGridVolumeErrorCodes | undefined) => {
  switch (code) {
    case 101:
      return t('cellGridVolume.error.101', { ns: 'skyviewVolumeCalculation' });
    case 102:
      return t('cellGridVolume.error.102', { ns: 'skyviewVolumeCalculation' });
    case 103:
      return t('cellGridVolume.error.103', { ns: 'skyviewVolumeCalculation' });
    case 200:
      return t('cellGridVolume.error.200', { ns: 'skyviewVolumeCalculation' });
    case 300:
      return t('cellGridVolume.error.300', { ns: 'skyviewVolumeCalculation' });
    case 301:
      return t('cellGridVolume.error.301', { ns: 'skyviewVolumeCalculation' });
    case 999:
    default:
      t('cellGridVolume.error.999', { ns: 'skyviewVolumeCalculation' });
  }
};

type DynamicAssetPropertiesCellGridVolume = NonNullable<
  DynamicAssetCommonPropertiesModel['cellGridVolumes']
>[0];

export class CellGridVolume {
  static fromPropertyArray(
    cellGridVolumes?: DynamicAssetPropertiesCellGridVolume[],
  ): CellGridVolume[] {
    if (cellGridVolumes && cellGridVolumes.length > 0) {
      return cellGridVolumes
        .map((properties) => new CellGridVolume(properties))
        .sort((a, b) => (Number(a.createdAt) > Number(b.createdAt) ? 1 : -1));
    }

    return [];
  }

  get displayDate() {
    return formattedDateTime(this.createdAt);
  }

  get errorText() {
    return (this.error?.code && errorTexts(this.error.code)) || '';
  }

  get statusMessage() {
    switch (this.status) {
      case 'PENDING':
        return t('cellGridVolume.status.pending', { ns: 'skyviewVolumeCalculation' });
      case 'RUNNING':
        return t('cellGridVolume.status.running', { ns: 'skyviewVolumeCalculation' });
      case 'COMPLETED':
        return t('cellGridVolume.status.completed', { ns: 'skyviewVolumeCalculation' });
      default:
        return t('cellGridVolume.status.failed', { ns: 'skyviewVolumeCalculation' });
    }
  }

  id?: string;
  name?: string;
  status?: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'START_FAILED';
  cut?: number;
  fill?: number;
  cellWidth?: number;
  cellDepth?: number;

  modelUrl?: string;
  createdAt?: Date;
  sourceAssetId?: string;
  sourcePointCloudId?: string;
  sourceTerrainModelGeoDataId?: string;
  offset?: Vector3;
  error?: {
    code: CellGridVolumeErrorCodes;
    readable: string;
  };

  constructor(properties: Partial<CellGridVolume | DynamicAssetPropertiesCellGridVolume>) {
    this.id = properties.id;
    this.name = properties.name;
    this.status = properties.status;
    this.cut = properties.cut;
    this.fill = properties.fill;
    this.cellWidth = properties.cellWidth;
    this.cellDepth = properties.cellDepth;
    this.modelUrl = properties.modelUrl;
    this.createdAt = properties.createdAt ? new Date(properties.createdAt) : undefined;
    this.sourceAssetId = properties.sourceAssetId;
    this.sourcePointCloudId = properties.sourcePointCloudId;
    this.sourceTerrainModelGeoDataId = properties.sourceTerrainModelGeoDataId;
    this.offset = properties.offset
      ? new Vector3(properties.offset.x, properties.offset.y, properties.offset.z)
      : undefined;
    this.error = properties.error;
  }

  isProcessing() {
    return ['RUNNING', 'PENDING'].includes(this.status!);
  }

  isCompleted() {
    return this.status === 'COMPLETED';
  }

  getSourceName() {
    if (this.sourceAssetId) {
      return (
        AssetManager.instance
          .getAssets()
          .find((sourceAsset) => sourceAsset.assetId === this.sourceAssetId)?.name ??
        t('volumes.section.sourceSurfaceNotFoundFallbackName', { ns: 'skyviewAssetMenu' })
      );
    }

    if (this.sourceTerrainModelGeoDataId) {
      const asset = AssetManager.instance
        .getAssets()
        .find(
          (sourceAsset) =>
            sourceAsset instanceof TerrainModel &&
            sourceAsset.geoDataId === this.sourceTerrainModelGeoDataId,
        ) as TerrainModel | undefined;

      return isDefined(asset) && asset.name.trim().length > 0
        ? t('cellGridVolume.sourceName.terrainModelWithAssetName', {
            ns: 'skyviewVolumeCalculation',
            assetName: asset.name,
          })
        : t('cellGridVolume.sourceName.terrainModelWithoutAssetName', {
            ns: 'skyviewVolumeCalculation',
          });
    }

    const foundContainer = this.sourcePointCloudId
      ? ProjectGeodataManager.instance()
          .getTimelineContainers()
          .find((container) => container.pointCloud === this.sourcePointCloudId)
      : undefined;

    if (foundContainer) {
      return t('cellGridVolume.sourceName.pointCloudWithTimeline', {
        ns: 'skyviewVolumeCalculation',
        timelineDate: foundContainer.displayDate,
        timelineIndex: foundContainer.containerIndex,
      });
    }

    return this.sourcePointCloudId
      ? t('cellGridVolume.sourceName.pointCloudDetachedFromTimeline', {
          ns: 'skyviewVolumeCalculation',
        })
      : t('cellGridVolume.sourceName.pointCloudWithoutTimeline', {
          ns: 'skyviewVolumeCalculation',
        });
  }
}
